// src/pages/business/mpesa/MpesaSetupPage.tsx
// Web port of BusinessMpesaSetupScreen.tsx
// Allows business owners to configure M-Pesa via Daraja or Automated Bank Payout Gateway.
// Note: Designed to be reusable for cooperative and multi-business architectures.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ShieldCheck, Server, Smartphone, Loader2, CheckCircle, Lock,
  ChevronDown, Copy, XCircle, AlertTriangle, RefreshCw, AlertCircle, Building2
} from 'lucide-react';
import BusinessPageLayout from '@/components/business/BusinessPageLayout';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

// ─── Types ───────────────────────────────────────────────────────────────────

type Gateway = 'daraja' | 'automated';
type AccountType = 'till' | 'paybill';
type SetupStep = 'account' | 'credentials';

// ─── Banks for Automated Payout Gateway ─────────────────────────────────────────────
const BANKS = [
  // Mobile Money
  { id: 'MPESA_TILL', name: 'M-PESA Buy Goods (Till Number)' },
  { id: 'MPESA_PAYBILL', name: 'M-PESA Paybill' },
  // Traditional Banks
  { id: '01', name: 'KCB Bank' },
  { id: '02', name: 'Standard Chartered Bank' },
  { id: '03', name: 'Absa Bank Kenya' },
  { id: '05', name: 'Bank of India' },
  { id: '06', name: 'Bank of Baroda' },
  { id: '07', name: 'NCBA Bank' },
  { id: '09', name: 'Stanbic Bank' },
  { id: '10', name: 'I&M Bank' },
  { id: '11', name: 'Co-operative Bank' },
  { id: '12', name: 'National Bank of Kenya' },
  { id: '14', name: 'M-Oriental Bank' },
  { id: '16', name: 'Prime Bank' },
  { id: '17', name: 'Credit Bank' },
  { id: '18', name: 'Victoria Commercial Bank' },
  { id: '23', name: 'Consolidated Bank' },
  { id: '31', name: 'Development Bank of Kenya' },
  { id: '34', name: 'Kingdom Bank' },
  { id: '35', name: 'ABC Bank' },
  { id: '41', name: 'Paramount Bank' },
  { id: '43', name: 'Bank of Africa' },
  { id: '44', name: 'Ecobank' },
  { id: '49', name: 'Spire Bank' },
  { id: '53', name: 'Guaranty Trust Bank (GTB)' },
  { id: '55', name: 'SBM Bank Kenya' },
  { id: '59', name: 'Middle East Bank' },
  { id: '61', name: 'Housing Finance (HFC)' },
  { id: '63', name: 'Diamond Trust Bank (DTB)' },
  { id: '66', name: 'Sidian Bank' },
  { id: '68', name: 'Equity Bank' },
  { id: '70', name: 'Family Bank' },
  { id: '72', name: 'Gulf African Bank' },
  { id: '74', name: 'Premier Bank (First Community)' },
  { id: '76', name: 'UBA Kenya Bank' }
].sort((a, b) => a.name.localeCompare(b.name));

// ─── Component ────────────────────────────────────────────────────────────────

const MpesaSetupPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [setupStatus, setSetupStatus] = useState<any>(null);
  const [activeGateway, setActiveGateway] = useState<Gateway>('automated');
  const [currentStep, setCurrentStep] = useState<SetupStep>('account');

  // Daraja state
  const [accountType, setAccountType] = useState<AccountType>('till');
  const [shortcode, setShortcode] = useState('');
  const [darajaBusinessName, setDarajaBusinessName] = useState('');
  const [darajaPhone, setDarajaPhone] = useState('');
  const [darajaAccountNo, setDarajaAccountNo] = useState('');
  const [consumerKey, setConsumerKey] = useState('');
  const [consumerSecret, setConsumerSecret] = useState('');
  const [passkey, setPasskey] = useState('');
  const [businessShortcode, setBusinessShortcode] = useState('');
  const [environment, setEnvironment] = useState<'sandbox' | 'production'>('sandbox');

  // Automated Gateway state
  const [automatedName, setAutomatedName] = useState('');
  const [automatedAccountNo, setAutomatedAccountNo] = useState('');
  const [automatedBankId, setAutomatedBankId] = useState('');

  // Import flow (Automated Gateway)
  const [importState, setImportState] = useState<{ available: boolean, sourceId: string, sourceType: string, sourceName: string } | null>(null);

  // STK Test
  const [testPhone, setTestPhone] = useState('254');
  const [testStatus, setTestStatus] = useState<'idle' | 'waiting' | 'success' | 'failed'>('idle');
  const tumaPollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/business/${businessId}/mpesa/status`);
      setSetupStatus(data);
      if (data.mpesaSettings) {
        setAccountType(data.mpesaSettings.account_type || 'till');
        setShortcode(data.mpesaSettings.shortcode || '');
        setDarajaBusinessName(data.mpesaSettings.business_name || '');
        setDarajaPhone(data.mpesaSettings.phone_number || '');
        setDarajaAccountNo(data.mpesaSettings.account_number || '');
      }
      if (data.setupStage === 'credentials' || data.hasMpesa) {
        setCurrentStep('credentials');
      }
      if (data.bankPayoutSettings) {
        setActiveGateway('automated');
      } else if (data.hasMpesa) {
        setActiveGateway('daraja');
      }
    } catch {
      toast.error('Error', 'Failed to load M-Pesa status');
    } finally {
      setInitialLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchStatus();
    return () => { if (tumaPollTimer.current) clearInterval(tumaPollTimer.current); };
  }, [fetchStatus]);

  // ─── Daraja Flow ─────────────────────────────────────────────────────────────
  const saveDarajaAccount = async () => {
    if (!shortcode || !darajaBusinessName) {
      toast.warning('Required', 'Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      await apiClient.post(`/business/${businessId}/mpesa/setup`, {
        accountType, shortcode, businessName: darajaBusinessName,
        phoneNumber: darajaPhone || undefined, accountNumber: darajaAccountNo || undefined,
      });
      toast.success('Saved', 'Account details saved. Please enter your API credentials.');
      setCurrentStep('credentials');
      fetchStatus();
    } catch (e: any) {
      toast.error('Save Failed', e.response?.data?.error || 'Could not save account details');
    } finally {
      setLoading(false);
    }
  };

  const saveDarajaCredentials = async () => {
    if (!consumerKey || !consumerSecret || !passkey || !businessShortcode) {
      toast.warning('Required', 'Please fill all credential fields');
      return;
    }
    setLoading(true);
    try {
      await apiClient.post(`/business/${businessId}/mpesa/credentials`, {
        consumerKey, consumerSecret, passkey, shortcode, businessShortcode,
        accountType, businessName: darajaBusinessName, environment,
      });
      toast.success('Activated!', 'Your Daraja M-Pesa integration is now active.');
      fetchStatus();
      setConsumerKey(''); setConsumerSecret(''); setPasskey(''); setBusinessShortcode('');
    } catch (e: any) {
      toast.error('Setup Failed', e.response?.data?.error || 'Failed to verify credentials');
    } finally {
      setLoading(false);
    }
  };

  // ─── Automated Gateway Flow ─────────────────────────────────────────────────────────────
  const requestImport = async () => {
    if (!importState) return;
    setLoading(true);
    try {
      await apiClient.post(`/business/${businessId}/tuma/request-import`, {
        sourceId: importState.sourceId, sourceType: importState.sourceType, accountNumber: automatedAccountNo,
      });
      toast.success('Request Sent', `Import request sent to ${importState.sourceName}.`);
      setImportState(null);
    } catch (e: any) {
      toast.error('Failed', e.response?.data?.error || 'Could not send import request');
    } finally {
      setLoading(false);
    }
  };

  const saveAutomatedGateway = async () => {
    if (!automatedName.trim() || !automatedAccountNo.trim() || !automatedBankId) {
      toast.warning('Required', 'Please fill all fields');
      return;
    }
    setLoading(true);
    setImportState(null);
    try {
      await apiClient.post(`/business/${businessId}/payhero/credentials`, {
        businessName: automatedName.trim(), accountNumber: automatedAccountNo.trim(), bankId: automatedBankId,
      });
      toast.success('Activated!', 'Your automated M-Pesa gateway is now active.');
      fetchStatus();
    } catch (e: any) {
      if (e.response?.status === 409 && e.response?.data?.importAvailable) {
        setImportState(e.response.data);
      } else {
        toast.error('Setup Failed', e.response?.data?.error || 'Failed to activate automated gateway');
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Testing STK Push ────────────────────────────────────────────────────────
  const handleTestPush = async () => {
    let formatted = testPhone.replace(/\s/g, '').replace('+', '');
    if (formatted.startsWith('0')) formatted = '254' + formatted.substring(1);
    if (!formatted.startsWith('254') || formatted.length < 12) {
      toast.warning('Invalid', 'Enter a valid number, e.g. 0712 345 678');
      return;
    }
    setTestStatus('waiting');
    try {
      const { data } = await apiClient.post(`/business/${businessId}/tuma/test-push`, { phoneNumber: formatted });
      startTumaPolling(data.checkout_request_id);
    } catch (e: any) {
      setTestStatus('failed');
      toast.error('Test Failed', e.response?.data?.error || 'Could not send STK push');
    }
  };

  const startTumaPolling = (cid: string) => {
    if (tumaPollTimer.current) clearInterval(tumaPollTimer.current);
    let elapsed = 0;
    tumaPollTimer.current = setInterval(async () => {
      elapsed += 3;
      if (elapsed >= 90) {
        if (tumaPollTimer.current) clearInterval(tumaPollTimer.current);
        setTestStatus('failed');
        toast.warning('Timeout', 'No payment confirmation received.');
        return;
      }
      try {
        const { data } = await apiClient.get(`/business/${businessId}/tuma/test-status/${cid}`);
        if (data.status === 'paid' || data.status === 'success') {
          if (tumaPollTimer.current) clearInterval(tumaPollTimer.current);
          setTestStatus('success');
          toast.success('Success!', 'Test payment received successfully.');
        } else if (data.status === 'failed') {
          if (tumaPollTimer.current) clearInterval(tumaPollTimer.current);
          setTestStatus('failed');
          toast.error('Failed', data.result_desc || 'Payment failed.');
        }
      } catch { /* wait */ }
    }, 3000);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied', 'Copied to clipboard');
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  if (initialLoading) {
    return (
      <BusinessPageLayout title="M-Pesa Setup">
        <div className="flex justify-center py-24"><Loader2 size={36} className="animate-spin text-primary" /></div>
      </BusinessPageLayout>
    );
  }

  const activePayout = setupStatus?.bankPayoutSettings;
  const isDarajaActive = !!setupStatus?.hasMpesa;
  const inputClass = "w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/30";

  return (
    <BusinessPageLayout title="Payment Settings">
      <div className="max-w-4xl mx-auto px-4 py-6">
        
        {/* Gateway Selector */}
        <div className="flex p-1 rounded-2xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border shadow-sm mb-8">
          <button
            onClick={() => setActiveGateway('automated')}
            className={`flex-1 flex flex-col items-center gap-1 px-4 py-3 rounded-xl transition-all ${
              activeGateway === 'automated' ? 'bg-primary text-white shadow-md' : 'text-light-subtle dark:text-dark-subtle hover:text-light-text dark:hover:text-dark-text'
            }`}
          >
            <ShieldCheck size={20} />
            <span className="font-bold text-sm">Direct Bank Payout</span>
          </button>
          <button
            onClick={() => setActiveGateway('daraja')}
            className={`flex-1 flex flex-col items-center gap-1 px-4 py-3 rounded-xl transition-all ${
              activeGateway === 'daraja' ? 'bg-primary text-white shadow-md' : 'text-light-subtle dark:text-dark-subtle hover:text-light-text dark:hover:text-dark-text'
            }`}
          >
            <Server size={20} />
            <span className="font-bold text-sm">Daraja Direct</span>
          </button>
        </div>

        {/* ─── AUTOMATED GATEWAY TAB ──────────────────────────────────────────────────────── */}
        {activeGateway === 'automated' && (
          <div className="space-y-6">
            {activePayout ? (
              <div className="bg-[#0F172A] rounded-2xl p-6 border border-[#10B981] shadow-lg shadow-[#10B981]/10">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={24} className="text-[#10B981]" />
                    <span className="text-[#10B981] font-bold uppercase tracking-wider text-sm">Active Connection</span>
                  </div>
                  <span className="bg-[#10B981]/20 text-[#10B981] text-xs font-bold px-3 py-1 rounded-full">Verified</span>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Bank Name</p>
                    <p className="text-white text-lg font-semibold">{BANKS.find(b => b.id === activePayout.bank_code)?.name || activePayout.bank_code}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Account Name</p>
                    <p className="text-white text-lg font-semibold">{activePayout.account_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Account Number</p>
                    <p className="text-white text-lg font-semibold tracking-widest">•••• •••• {activePayout.account_number.slice(-4)}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-[#052E16] rounded-2xl p-6 text-center border border-[#10B981] shadow-lg shadow-[#10B981]/10">
                <div className="w-16 h-16 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-4">
                  <Lock size={32} className="text-[#10B981]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Bank-Grade Payout Security</h3>
                <p className="text-[#A7F3D0] text-sm mb-4 max-w-md mx-auto">
                  Your details are encrypted. We route M-Pesa payments directly to your verified bank account via PesaLink.
                </p>
              </div>
            )}

            <div className="bg-light-card dark:bg-dark-card rounded-2xl p-6 border border-light-border dark:border-dark-border shadow-sm">
              <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-6">
                {activePayout ? 'Update Settlement Account' : 'Settlement Account Details'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-2">Account Name</label>
                  <input type="text" value={automatedName} onChange={e => setAutomatedName(e.target.value)} placeholder="e.g. Majengo Stores" className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-2">Select Destination</label>
                  <div className="relative">
                    <select value={automatedBankId} onChange={e => setAutomatedBankId(e.target.value)} className={`${inputClass} appearance-none cursor-pointer`}>
                      <option value="" disabled>Choose Destination...</option>
                      <optgroup label="Mobile Money">
                        {BANKS.filter(b => b.id.startsWith('MPESA')).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </optgroup>
                      <optgroup label="Banks">
                        {BANKS.filter(b => !b.id.startsWith('MPESA')).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </optgroup>
                    </select>
                    <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-2">
                    {automatedBankId === 'MPESA_TILL' ? 'Till Number' : automatedBankId === 'MPESA_PAYBILL' ? 'Paybill Number' : 'Account Number'}
                  </label>
                  <input type="text" value={automatedAccountNo} onChange={e => setAutomatedAccountNo(e.target.value)} placeholder={automatedBankId?.startsWith('MPESA') ? "e.g. 123456" : "e.g. 0110000000"} className={inputClass} />
                </div>

                {importState ? (
                  <div className="bg-primary/10 rounded-xl p-4 mt-2">
                    <p className="font-bold text-light-text dark:text-dark-text mb-1">Account Exists</p>
                    <p className="text-sm text-light-text dark:text-dark-text mb-4">Registered to <strong>{importState.sourceName}</strong>. Request permission to import.</p>
                    <div className="flex gap-2">
                      <button onClick={requestImport} disabled={loading} className="flex-1 py-2.5 rounded-lg bg-primary text-white font-bold text-sm">Send Request</button>
                      <button onClick={() => setImportState(null)} className="flex-1 py-2.5 rounded-lg bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text font-bold text-sm border border-light-border dark:border-dark-border">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={saveAutomatedGateway} disabled={loading} className="w-full mt-2 py-3.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold transition flex items-center justify-center">
                    {loading ? <Loader2 size={18} className="animate-spin" /> : activePayout ? 'Update Bank Details' : 'Activate Gateway'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ─── DARAJA TAB ───────────────────────────────────────────────────────── */}
        {activeGateway === 'daraja' && (
          <div className="space-y-6">
            
            {isDarajaActive && (
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl p-6 border border-emerald-200 dark:border-emerald-700">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle size={24} className="text-emerald-500" />
                  <h3 className="text-lg font-bold text-emerald-700 dark:text-emerald-300">Daraja M-Pesa Active</h3>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm text-emerald-800 dark:text-emerald-200">
                  <div><span className="font-semibold block">Account Type:</span> {setupStatus.mpesaSettings?.account_type}</div>
                  <div><span className="font-semibold block">Your Number:</span> {setupStatus.mpesaSettings?.shortcode}</div>
                  <div><span className="font-semibold block">Daraja Shortcode:</span> {setupStatus.mpesaSettings?.business_shortcode}</div>
                  <div><span className="font-semibold block">Environment:</span> {setupStatus.mpesaSettings?.environment}</div>
                </div>
              </div>
            )}

            <div className="bg-light-card dark:bg-dark-card rounded-2xl p-6 border border-light-border dark:border-dark-border shadow-sm">
              <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-4">1. Account Details</h3>
              <div className="flex gap-2 mb-4">
                {(['till', 'paybill'] as AccountType[]).map(t => (
                  <button key={t} onClick={() => setAccountType(t)} className={`flex-1 py-2.5 rounded-xl text-sm font-bold border-2 transition ${accountType === t ? 'border-primary bg-primary/10 text-primary' : 'border-light-border dark:border-dark-border text-light-subtle dark:text-dark-subtle'}`}>
                    {t === 'till' ? 'Till Number' : 'PayBill Number'}
                  </button>
                ))}
              </div>
              <div className="space-y-4 mb-4">
                <div>
                  <label className="block text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase mb-1.5">{accountType === 'till' ? 'Till Number' : 'PayBill Number'} *</label>
                  <input type="text" value={shortcode} onChange={e => setShortcode(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase mb-1.5">Business Name *</label>
                  <input type="text" value={darajaBusinessName} onChange={e => setDarajaBusinessName(e.target.value)} className={inputClass} />
                </div>
              </div>
              <button onClick={saveDarajaAccount} disabled={loading} className="w-full py-3 rounded-xl bg-primary text-white font-bold transition flex items-center justify-center">
                {loading && currentStep === 'account' ? <Loader2 size={18} className="animate-spin" /> : 'Save Details'}
              </button>
            </div>

            {currentStep === 'credentials' && (
              <div className="bg-light-card dark:bg-dark-card rounded-2xl p-6 border border-light-border dark:border-dark-border shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-light-text dark:text-dark-text">2. API Credentials</h3>
                  <div className="flex rounded-lg border border-light-border dark:border-dark-border overflow-hidden">
                    {(['sandbox', 'production'] as const).map(env => (
                      <button key={env} onClick={() => setEnvironment(env)} className={`px-3 py-1.5 text-xs font-bold ${environment === env ? 'bg-primary text-white' : 'bg-light-bg dark:bg-dark-bg text-light-subtle dark:text-dark-subtle'}`}>
                        {env === 'sandbox' ? 'Test' : 'Live'}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase mb-1.5">Daraja Business Shortcode *</label>
                    <input type="text" value={businessShortcode} onChange={e => setBusinessShortcode(e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase mb-1.5">Consumer Key *</label>
                    <input type="password" value={consumerKey} onChange={e => setConsumerKey(e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase mb-1.5">Consumer Secret *</label>
                    <input type="password" value={consumerSecret} onChange={e => setConsumerSecret(e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase mb-1.5">Passkey *</label>
                    <input type="password" value={passkey} onChange={e => setPasskey(e.target.value)} className={inputClass} />
                  </div>
                </div>
                <button onClick={saveDarajaCredentials} disabled={loading} className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold transition flex items-center justify-center">
                  {loading && currentStep === 'credentials' ? <Loader2 size={18} className="animate-spin" /> : 'Activate Daraja API'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ─── TEST PUSH SECTION (Visible if active) ───────────────────────────── */}
        {(activePayout || isDarajaActive) && (
          <div className="mt-6 bg-light-card dark:bg-dark-card rounded-2xl p-6 border border-light-border dark:border-dark-border shadow-sm">
            <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-2">Test Payment</h3>
            <p className="text-sm text-light-subtle dark:text-dark-subtle mb-4">Send a KES 1 test prompt to verify your setup.</p>
            <div className="flex gap-2">
              <input type="tel" value={testPhone} onChange={e => setTestPhone(e.target.value)} placeholder="Phone number" className={`${inputClass} flex-1`} />
              <button onClick={handleTestPush} disabled={testStatus === 'waiting'} className="px-6 py-3 rounded-xl bg-primary text-white font-bold transition flex items-center gap-2 disabled:opacity-50">
                {testStatus === 'waiting' ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : <><Smartphone size={16} /> Send Test</>}
              </button>
            </div>
            {testStatus === 'success' && <p className="mt-3 text-sm font-bold text-emerald-500 flex items-center gap-1"><CheckCircle size={14}/> Test successful!</p>}
            {testStatus === 'failed' && <p className="mt-3 text-sm font-bold text-red-500 flex items-center gap-1"><XCircle size={14}/> Test failed. Check setup.</p>}
          </div>
        )}
      </div>
    </BusinessPageLayout>
  );
};

export default MpesaSetupPage;
